import { DataCategory } from '../../../../shared/api/api';
import React, { useMemo } from 'react';

import './FilterFields.scss';
import { Control } from 'react-hook-form';
import { useDataCategories } from '../../../../shared/api/apiHooks';
import { findNestedCategory } from '../../Categories/helpers';
import { filterFields } from './dataExplorerFieldsConfig';
import { addModifiers } from '../../../../shared/helpers';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { formatMessageHelper, FormattedMessageHelper } from '../../../../shared/helpers/internationalization';
import { useToggle } from 'react-use';
import { DatasetPathCategoryParams } from '../../dataExplorerRouterPaths';
import { TableDataCategory } from '../../Categories/types';
import { useFilterController } from '../useFilterForm';
import { FiltersControl } from '../types';
import { getInputId } from './getInputId';

type Props = {
  onOpenModal: () => void;
  selectedCategory?: DataCategory;
  control: Control;
} & InjectedIntlProps;

const CategoryFieldIntl = ({ onOpenModal, selectedCategory, control, intl }: Props) => {
  const onCategoryClick = () => {
    onOpenModal();
  };

  const categoryField = useFilterController({ control, name: filterFields.category.name });
  const subcategoryField = useFilterController({ control, name: filterFields.subcategory.name });
  //as the response for category name may be still loading, we show the first non-null string here:
  const label = [
    selectedCategory?.name,
    subcategoryField.field.value,
    categoryField.field.value,
    formatMessageHelper(intl, 'dataExplorer.filters.categoryFieldAllLabel'),
  ].find(val => !!val);

  const onResetClick = () => {
    categoryField.field.onChange({ target: { value: filterFields.category.defaultValue } });
    subcategoryField.field.onChange({ target: { value: filterFields.subcategory.defaultValue } });
  };
  const resetButtonClassName = addModifiers('data-category-field__clear', !selectedCategory ? ['hidden'] : []);

  return (
    <div className="data-category-field">
      <span className="data-category-field__label">
        <FormattedMessageHelper id="dataExplorer.filters.categoryLabel" />
      </span>{' '}
      <div className="data-category-field__container">
        <button data-testid={getInputId(filterFields.category.name)} className="data-category-field__button" onClick={onCategoryClick}>
          {label}
        </button>
        <button onClick={onResetClick} className={resetButtonClassName}></button>
      </div>
    </div>
  );
};

export const CategoryField = injectIntl(CategoryFieldIntl);

export function useFormDataCategoryField(control: FiltersControl) {
  const categoryField = useFilterController({ control, name: filterFields.category.name });
  const subCategoryField = useFilterController({ control, name: filterFields.subcategory.name });

  const categoryValue = categoryField.field.value;
  const subCategoryValue = subCategoryField.field.value;

  const activeValue = categoryValue || subCategoryValue;

  const { data } = useDataCategories();
  const selectedCategory = useMemo(() => (activeValue ? findNestedCategory(data ?? [], activeValue) : undefined), [activeValue, data]);

  const onCategoryClick = (category: TableDataCategory) => {
    const isSubcategory = category._parent;

    categoryField.field.onChange({ target: { value: isSubcategory ? '' : category.id } });
    subCategoryField.field.onChange({ target: { value: isSubcategory ? category.id : '' } });
    control.flushDebounce();
  };
  return { selectedCategory, onCategoryClick };
}

export function useDataCategoryField(onClick: ({ categoryId, subcategoryId }: DatasetPathCategoryParams) => void) {
  const [isCategoriesOpen, toggleCategoriesOpen] = useToggle(false);

  useDataCategories();

  const onCategoryClick = (category: TableDataCategory) => {
    const isSubcategory = category._parent;
    onClick({ categoryId: isSubcategory ? undefined : category.id, subcategoryId: isSubcategory ? category.id : undefined });
  };

  return { onCategoryClick, isCategoriesOpen, toggleCategoriesOpen };
}
